<!-- Page Search Template (injected into center zone of toolbar) -->
<ng-template #toolbarSearch>
  <ui-input
    type="search"
    [value]="searchTerm"
    (valueChange)="onSearch($event)"
    placeholder="Search recipes..."
    [clearable]="true"
    (cleared)="clearSearch()"
    [fullWidth]="true"
    ariaLabel="Search recipes">
  </ui-input>
</ng-template>

<!-- Toolbar actions (Import / New) are registered declaratively in the
     component via PageHeaderService.setPageActions(...). -->

<!-- Import from URL Modal -->
<ui-modal
  #importUiModal
  title="Import recipe"
  size="md"
  [showCloseButton]="true"
  [closeOnBackdrop]="true"
  (modalClosed)="onImportDialogDismissed()">

  <div class="space-y-3">
    <p class="text-sm text-base-content/70">
      Paste a recipe URL and we’ll import it in the background.
    </p>

    <ui-input
      type="text"
      [value]="importUrl()"
      (valueChange)="onImportUrlChange($event)"
      placeholder="https://…"
      [fullWidth]="true"
      ariaLabel="Recipe URL">
    </ui-input>

    @if (importError()) {
      <div class="alert alert-error" role="alert">
        {{ importError() }}
      </div>
    }

    @if (importJob()) {
      <div class="border border-base-300 rounded-lg p-3">

        @if (isJobRunning(importJob())) {
          <!-- Visual loading indicator: the captured page shown in a "fake
               browser", with a laser scan line sweeping up and down while the
               AI extracts the recipe. Exits once the job reaches draft_ready /
               succeeded / failed. -->
          <div class="import-browser">
            <div class="import-browser__chrome">
              <span class="import-browser__dot import-browser__dot--red"></span>
              <span class="import-browser__dot import-browser__dot--amber"></span>
              <span class="import-browser__dot import-browser__dot--green"></span>
              <div class="import-browser__address">
                {{ importJob()?.target_url || 'about:blank' }}
              </div>
            </div>

            <div
              class="import-browser__viewport"
              [class.is-scanning]="isScanningPage()">
              @if (importThumbnailUrl(); as thumb) {
                <img
                  class="import-browser__page"
                  [src]="thumb"
                  alt="Captured recipe page" />
              } @else {
                <!-- Phase 1: opening page — animated skeleton until the crawler
                     has captured the page. -->
                <div class="import-browser__skeleton">
                  <div class="import-browser__skeleton-line w-3/5"></div>
                  <div class="import-browser__skeleton-line w-full"></div>
                  <div class="import-browser__skeleton-line w-11/12"></div>
                  <div class="import-browser__skeleton-line w-4/5"></div>
                </div>
              }

              @if (isScanningPage()) {
                <div class="import-browser__scanline" aria-hidden="true"></div>
              }
            </div>
          </div>

          <div class="mt-3 text-center">
            <div class="flex items-center justify-center gap-2 font-medium text-sm">
              <span class="loading loading-spinner loading-xs"></span>
              {{ isOpeningPage() ? 'Opening page…' : 'Scanning recipe…' }}
            </div>
            @if (importJob()?.state_message) {
              <div class="mt-0.5 text-xs text-base-content/60 truncate">
                {{ importJob()?.state_message }}
              </div>
            }
          </div>
        } @else {
          <!-- Completed / draft states keep the compact status header. -->
          <div class="flex items-center justify-between gap-3">
            <div class="min-w-0">
              <div class="font-medium">Status: {{ importJob()?.status || '—' }}</div>
              <div class="text-sm text-base-content/60 truncate">{{ importJob()?.state_message || '' }}</div>
            </div>
          </div>
        }

        <!-- Draft review: pipeline finished, nothing persisted yet — user must
             review and explicitly save. Reuses the same card the household
             chat assistant uses for its own propose_recipe drafts. -->
        @if (importJobDraft(); as draft) {
          <div class="mt-3 max-h-[50vh] overflow-y-auto">
            <ui-recipe-preview [recipe]="draft"></ui-recipe-preview>
          </div>

          @if (proposedImages().length > 0) {
            <div class="mt-4">
              <div class="text-sm font-semibold mb-1">Images</div>
              <div class="text-xs text-base-content/60 mb-2">
                Tap the star to choose the title image, and the checkbox to keep others.
              </div>
              <div class="proposed-images-grid">
                @for (img of proposedImages(); track img.name) {
                  <div
                    class="proposed-image"
                    [class.is-title]="img.isTitle"
                    [class.is-kept]="img.keep">
                    <div class="proposed-image__frame">
                      @if (img.objectUrl) {
                        <img [src]="img.objectUrl" class="proposed-image__img" alt="Proposed recipe image"/>
                      } @else {
                        <div class="proposed-image__skeleton">
                          <span class="loading loading-spinner loading-sm"></span>
                        </div>
                      }

                      <button
                        type="button"
                        class="proposed-image__star"
                        [class.active]="img.isTitle"
                        [attr.aria-pressed]="img.isTitle"
                        title="Use as title image"
                        (click)="setProposedTitle(img.name)">
                        <fa-icon [icon]="faStar"></fa-icon>
                      </button>

                      @if (img.isTitle) {
                        <div class="proposed-image__title-banner">Title</div>
                      }
                    </div>

                    <label class="proposed-image__keep">
                      <input
                        type="checkbox"
                        class="checkbox checkbox-xs"
                        [checked]="img.keep"
                        (change)="toggleKeepProposedImage(img.name)"/>
                      <span>Keep</span>
                    </label>
                  </div>
                }
              </div>
            </div>
          }
        }
      </div>
    }
  </div>

  <div modal-footer class="flex gap-2 justify-end">
    <ui-button variant="ghost" (clicked)="closeImportFromUrlModal()">
      Close
    </ui-button>

    @if (isDraftReady(importJob())) {
      <ui-button
        variant="primary"
        [disabled]="importIsSaving()"
        (clicked)="saveImportedDraft()">
        @if (importIsSaving()) {
          <span class="loading loading-spinner loading-sm mr-2"></span>
        }
        Rezept speichern
      </ui-button>
    } @else if (canViewImportedRecipe(importJob())) {
      <ui-button variant="primary" (clicked)="viewImportedRecipe(importJob())">
        View Recipe
      </ui-button>
    } @else {
      <ui-button
        variant="primary"
        [disabled]="importIsStarting() || importIsPolling()"
        (clicked)="startImportFromUrl()">
        @if (importIsStarting() || importIsPolling()) {
          <span class="loading loading-spinner loading-sm mr-2"></span>
        }
        Import
      </ui-button>
    }
  </div>
</ui-modal>

<!-- Filters -->
<div class="mb-3 flex flex-wrap items-center gap-3">
  <ui-button-group [fullWidthMobile]="true">
    <ui-button
      [variant]="view() === 'all' ? 'primary' : 'ghost'"
      size="sm"
      (clicked)="setView('all')"
      [disabled]="isLoading()"
      title="Show all recipes">
      <fa-icon [icon]="faUsers"></fa-icon>
      <span class="ml-1">All</span>
    </ui-button>

    <ui-button
      [variant]="view() === 'mine' ? 'primary' : 'ghost'"
      size="sm"
      (clicked)="setView('mine')"
      [disabled]="isLoading()"
      title="Show only my recipes">
      <fa-icon [icon]="faUser"></fa-icon>
      <span class="ml-1">Mine</span>
    </ui-button>
  </ui-button-group>

  <!-- Facet filters (course / season / dish type). Filter by key, render label. -->
  <app-recipe-facet-filter-bar
    [catalogue]="facetCatalogue()"
    [course]="courseFilter()"
    [season]="seasonFilter()"
    [dishType]="dishTypeFilter()"
    [disabled]="isLoading()"
    (courseChange)="onCourseFilterChange($event)"
    (seasonChange)="onSeasonFilterChange($event)"
    (dishTypeChange)="onDishTypeFilterChange($event)"
    (clear)="clearFacetFilters()"/>

  <!-- List / grid layout toggle, pushed to the right end of the filter row. -->
  <ui-button-group class="ml-auto">
    <ui-button
      [variant]="viewMode() === 'list' ? 'primary' : 'ghost'"
      size="sm"
      (clicked)="setViewMode('list')"
      title="List view"
      ariaLabel="List view">
      <fa-icon [icon]="faList"></fa-icon>
    </ui-button>
    <ui-button
      [variant]="viewMode() === 'grid' ? 'primary' : 'ghost'"
      size="sm"
      (clicked)="setViewMode('grid')"
      title="Grid view"
      ariaLabel="Grid view">
      <fa-icon [icon]="faTableCells"></fa-icon>
    </ui-button>
  </ui-button-group>
</div>

@if (errorMessage()) {
  <div class="alert alert-error mb-3" role="alert">
    {{ errorMessage() }}
  </div>
}

@if (loadingVisible()) {
  <div class="flex justify-center items-center min-h-[30vh]">
    <div class="flex items-center gap-3">
      <span class="loading loading-spinner loading-md"></span>
      <strong>Loading...</strong>
    </div>
  </div>
} @else if (!isLoading()) {
  @if (recipes().length === 0) {
    <div class="text-center py-8 text-base-content/60">
      No recipes found
    </div>
  } @else if (viewMode() === 'grid') {
    <div class="recipe-grid">
      @for (recipe of recipes(); track recipe.id) {
        <a class="recipe-card" [routerLink]="['/recipe', recipe.id]">
          <div class="recipe-card__media">
            @if (recipe.thumbnail_url) {
              <img [src]="recipe.thumbnail_url" [alt]="recipe.title" loading="lazy"/>
            } @else {
              <div class="recipe-card__placeholder" aria-hidden="true">
                <fa-icon [icon]="faUtensils"></fa-icon>
              </div>
            }
          </div>
          <div class="recipe-card__body">
            <div class="recipe-card__title">{{ recipe.title || 'Untitled' }}</div>

            <ui-star-bar
              size="sm"
              [avg]="recipe.rating?.average ?? 0"
              [count]="recipe.rating?.count ?? 0"
              [showValue]="(recipe.rating?.count ?? 0) > 0"/>

            @if (recipe.facets) {
              <app-recipe-facet-badges
                [facetsValue]="recipe.facets"
                [dishTypeLabels]="dishTypeLabels()"/>
            }

            <div class="recipe-card__meta">
              <span class="inline-flex items-center gap-1">
                <fa-icon [icon]="faUser" class="opacity-70"></fa-icon>
                {{ recipe.servings ?? '—' }}
              </span>
              <span>·</span>
              <span>{{ recipe.total_time_minutes ?? 0 }} min</span>
            </div>
          </div>
        </a>
      }
    </div>
  } @else {
    <ui-list variant="bordered">
      @for (recipe of recipes(); track recipe.id) {
        <ui-list-item [clickable]="true" [routerLink]="['/recipe', recipe.id]">
          <div class="recipe-list-row flex items-stretch justify-between gap-3 w-full">
            <div class="recipe-list-thumb">
              @if (recipe.thumbnail_url) {
                <img
                  [src]="recipe.thumbnail_url"
                  [alt]="recipe.title"
                  loading="lazy"/>
              } @else {
                <div class="recipe-list-thumb__placeholder" aria-hidden="true">
                  <fa-icon [icon]="faUtensils"></fa-icon>
                </div>
              }
            </div>
            <div class="min-w-0 flex-1">
              <div class="font-medium text-base-content truncate">
                {{ recipe.title || 'Untitled' }}
              </div>

              <div class="mt-1">
                <ui-star-bar
                  size="sm"
                  [avg]="recipe.rating?.average ?? 0"
                  [count]="recipe.rating?.count ?? 0"
                  [showValue]="(recipe.rating?.count ?? 0) > 0"/>
              </div>

              @if (recipe.facets) {
                <div class="mt-1">
                  <app-recipe-facet-badges
                    [facetsValue]="recipe.facets"
                    [dishTypeLabels]="dishTypeLabels()"/>
                </div>
              }

              <div class="text-sm text-base-content/60 mt-1 flex flex-wrap gap-x-2 gap-y-1">
                <span>Servings: {{ recipe.servings ?? '—' }}</span>
                <span class="opacity-60">•</span>
                <span>Prep: {{ recipe.prep_time_minutes ?? '—' }} min</span>
                <span class="opacity-60">•</span>
                <span>On table: {{ recipe.total_time_minutes ?? '—' }} min</span>
                @if (recipe.is_public) {
                  <span class="opacity-60">•</span>
                  <span>Public</span>
                }
                <span class="opacity-60">•</span>
                <span class="inline-flex items-center gap-1">
                  <fa-icon [icon]="sourceIcon(recipe.source)"></fa-icon>
                  @if (recipe.source?.type === 'import' && recipe.source?.url) {
                    <a
                      [href]="recipe.source?.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="link link-hover"
                      (click)="$event.stopPropagation()">
                      {{ sourceLabel(recipe.source) }}
                    </a>
                  } @else {
                    <span>{{ sourceLabel(recipe.source) }}</span>
                  }
                </span>
              </div>
            </div>

            <div class="text-sm text-base-content/60 text-right shrink-0 whitespace-nowrap">
              @if (recipe.updated_at) {
                <div>{{ recipe.updated_at | date: 'd. MMM y' }}</div>
              }

              @if (recipe.created_by) {
                <div class="text-sm text-base-content/60" [title]="recipe.created_by.user_email || ''">
                  {{ recipe.created_by.user_name }}
                </div>
              } @else {
                <div class="text-sm text-base-content/40 italic">Deleted user</div>
              }
            </div>
          </div>
        </ui-list-item>
      }
    </ui-list>
  }
}
